import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { downloadFile } from 'src/util/util';
import { translate } from 'src/hooks/useTranslate';

// Shared CanRisk download (TGCLOUD-1575). Previously duplicated verbatim in
// RiskFactorsMenu and ExportMenu, both of which swallowed everything the server
// said:
//
//   • The serializer drops relatives it cannot place in the pedigree and
//     reports them as warnings. On the download route those were reduced to a
//     COUNT in an X-CanRisk-Warnings header that neither caller ever read — so
//     a file that silently lost five relatives was indistinguishable from a
//     complete one.
//   • The response is requested as a Blob, so an error body is a Blob too.
//     `err.response.data.message` is therefore always undefined and every
//     failure — including the server's actionable 422 refusals — collapsed to
//     "Error exporting CanRisk pedigree".
//
// This helper reads the warning detail on success and parses the Blob body on
// failure so the real message reaches the clinician.

// Blob.text() is not universally available (absent in jsdom and older
// browsers), so fall back to FileReader rather than losing the message.
const blobToText = (blob) => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

// An axios error body arrives as a Blob under responseType:'blob'. Read it back
// to JSON; fall back to null so the caller can use its generic message.
const readBlobError = async (err) => {
  const body = err?.response?.data;
  if (!body) return null;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      return JSON.parse(await blobToText(body));
    } catch {
      return null;
    }
  }
  return typeof body === 'object' ? body : null;
};

const readList = (res, header) => {
  const raw = res?.headers?.[header];
  if (!raw) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const readWarnings = (res) => readList(res, 'x-canrisk-warning-detail');

// TGCLOUD-1700. Expected, harmless omissions arrive separately from warnings: a partner with
// no parents and no children recorded is not part of the family history CanRisk uses, so
// leaving them out costs the calculation nothing. Showing that in amber alongside real data
// loss is what taught clinicians to stop reading this channel — and after TGCLOUD-1699 it is
// the commonest message of all.
const readNotices = (res) => readList(res, 'x-canrisk-notice-detail');

export const downloadCanRisk = async ({
  userId,
  familyId,
  members,
  targetId,
  setSnack
}) => {
  if (!userId) {
    setSnack?.({
      status: true,
      msg: translate('Please save the pedigree first'),
      severity: 'error'
    });
    return;
  }

  try {
    const res = await axios.post(
      APICONSTANTS.getCanRiskData.replace(':user_id', userId),
      { members, targetId },
      { responseType: 'blob' }
    );

    downloadFile(res.data, familyId, 'txt');

    const warnings = readWarnings(res);
    const notices = readNotices(res);
    if (warnings.length) {
      // A lossy export must never look like a clean one. Lead with the count so
      // the message is useful even when the list is long. Notices ride along at the end so
      // the person count still reconciles, without inflating the warning count.
      setSnack?.({
        status: true,
        msg:
          warnings.length === 1
            ? translate('Exported with 1 warning: {{details}}', {
                details: [...warnings, ...notices].join(' ')
              })
            : translate('Exported with {{count}} warnings: {{details}}', {
                count: warnings.length,
                details: [...warnings, ...notices].join(' ')
              }),
        severity: 'warning'
      });
    } else if (notices.length) {
      setSnack?.({
        status: true,
        msg: translate('Exported. {{notices}}', { notices: notices.join(' ') }),
        severity: 'info'
      });
    }
  } catch (err) {
    const body = await readBlobError(err);
    setSnack?.({
      status: true,
      msg: body?.message || translate('Error exporting CanRisk pedigree'),
      severity: 'error'
    });
  }
};
